import React, { useState, useEffect } from "react";
import { Logo } from "../Logo";
import { Link } from "react-router-dom";
import { Button } from "../Button";
import { GetStartedModal } from "../dialogs/GetStartedModal";
import { cn } from "../../services/Utils";

/**
 * The nav floats from the first paint. Pages that lay their own content out
 * beneath it (the landing hero) pass `overlay`; everything else gets a spacer
 * so the flow below starts where it always did.
 */
const Header = ({ overlay = false }) => {
  const [isGetStartedOpen, setIsGetStartedOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleGetStartedClick = () => {
    setIsGetStartedOpen(true);
  };

  const renderNavContent = (compact) => (
    <>
      <div className="hidden lg:flex items-center gap-8"></div>

      <div className="relative ml-auto lg:ml-0 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
        <Link
          to="/"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          aria-label="Home"
        >
          <div className={compact ? "h-8" : "h-10"}>
            <Logo className="h-full w-auto" />
          </div>
          <span
            className={`font-semibold text-3xl lg:text-2xl`}
            style={{ fontFamily: "'Questrial', sans-serif" }}
          >
            fluvo
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-4">
        <Button
          size={compact ? "md" : "lg"}
          color="primary"
          onClick={handleGetStartedClick}
        >
          Get started
        </Button>

        <Link to="/login">
          <button className="hidden sm:block bg-white text-gray-700 border border-gray-300 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">
            Log In
          </button>
        </Link>
      </div>
    </>
  );

  return (
    <>
      {/* Floating nav — fixed from the first paint, deepening its shadow on scroll */}
      <div className="fixed top-4 left-1/2 z-50 -translate-x-1/2 w-full max-w-[1280px] px-4">
        <nav
          className={cn(
            // The right gutter matches the nav's vertical padding, so the
            // buttons sit tight against the pill's edge.
            "flex items-center justify-between bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full pl-6 pr-3 lg:pl-8 lg:pr-3 py-3 max-w-[1400px] mx-auto relative transition-shadow duration-300",
            scrolled ? "shadow-lg" : "shadow-sm",
          )}
        >
          {renderNavContent(false)}
        </nav>
      </div>

      {/* Keeps the nav's footprint in flow for pages that don't render beneath it */}
      {!overlay && <div aria-hidden="true" className="h-[88px] w-full" />}

      <GetStartedModal
        isOpen={isGetStartedOpen}
        onClose={() => setIsGetStartedOpen(false)}
      />
    </>
  );
};

export default Header;
